import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { clearOfflineCache, warmupCache } from '../lib/offline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Role constants
export const UserRole = {
    CHEF_DISPOSITIF: "chef_dispositif",
    LOGISTIQUE: "logistique",
    MOYEN: "moyen",
    GYMNASE_CHEF_POSTE: "gymnase_chef_poste",
    GYMNASE_ADMIN: "gymnase_admin",
    COMMUNE: "commune",
    SECOURISTE: "secouriste",
    EQUIPIER: "equipier",
    AGENT_COMMUNAUX: "agent_communaux",
    RESERVE_SECURITE_CIVILE: "reserve_securite_civile",
    ENTREPRISE_EXTERIEUR: "entreprise_exterieur",
    ENTREPRISE_EXTERIEUR_CONTRAT: "entreprise_exterieur_contrat",
    SECRETARIAT_COMMUNICATION: "secretariat_communication",
    POLICE_MUNICIPALE: "police_municipale",
    COS: "cos",
    SOUTIEN_LOG_COMMUNE: "soutien_log_commune",
    RENFORT_CARE: "renfort_care",
    OPERATEUR_RADIO: "operateur_radio",
    CHAUFFEUR_PL: "chauffeur_pl",
    SOUTIEN_PSY: "soutien_psy",
    PENDING: "pending"
};

export const RoleLabels = {
    [UserRole.CHEF_DISPOSITIF]: "Chef de Dispositif",
    [UserRole.LOGISTIQUE]: "Logistique",
    [UserRole.MOYEN]: "Moyens",
    [UserRole.GYMNASE_CHEF_POSTE]: "Chef de Poste CARE",
    [UserRole.GYMNASE_ADMIN]: "Admin CARE",
    [UserRole.COMMUNE]: "Service Communal",
    [UserRole.SECOURISTE]: "Secouriste",
    [UserRole.EQUIPIER]: "Equipier",
    [UserRole.AGENT_COMMUNAUX]: "Agent communaux",
    [UserRole.RESERVE_SECURITE_CIVILE]: "Reserve securite civile",
    [UserRole.ENTREPRISE_EXTERIEUR]: "Entreprise exterieur",
    [UserRole.ENTREPRISE_EXTERIEUR_CONTRAT]: "Entreprise ext. sous contrat",
    [UserRole.SECRETARIAT_COMMUNICATION]: "Secretariat / Communication",
    [UserRole.POLICE_MUNICIPALE]: "Police Municipale",
    [UserRole.COS]: "COS",
    [UserRole.SOUTIEN_LOG_COMMUNE]: "Soutien Log. Commune",
    [UserRole.RENFORT_CARE]: "Renfort CARE",
    [UserRole.OPERATEUR_RADIO]: "Operateur radio",
    [UserRole.CHAUFFEUR_PL]: "Chauffeur PL",
    [UserRole.SOUTIEN_PSY]: "Soutien Psy",
    [UserRole.PENDING]: "En attente"
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const axiosInstance = axios.create({
        baseURL: API,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    useEffect(() => {
        if (token) {
            axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
        } else {
            delete axiosInstance.defaults.headers.Authorization;
        }
    }, [token]);

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
            warmupCache();
        } catch (error) {
            console.error('Failed to fetch user:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                setToken(null);
                setUser(null);
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (email, password, name, role = UserRole.GYMNASE_ADMIN, assigned_center_id = null) => {
        const response = await axios.post(`${API}/auth/register`, { 
            email, password, name, role, assigned_center_id 
        });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        clearOfflineCache();
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    // Role checks
    const isChefDispositif = user?.role === UserRole.CHEF_DISPOSITIF;
    const isLogistique = user?.role === UserRole.LOGISTIQUE || isChefDispositif;
    const isMoyen = user?.role === UserRole.MOYEN || isChefDispositif;
    const isGymnaseChefPoste = user?.role === UserRole.GYMNASE_CHEF_POSTE;
    const isGymnaseAdmin = user?.role === UserRole.GYMNASE_ADMIN || isGymnaseChefPoste;
    const isCommune = user?.role === UserRole.COMMUNE;
    
    // Permission checks
    const canManagePersons = isChefDispositif || isMoyen || isGymnaseAdmin || isGymnaseChefPoste;
    const canManageLogistics = isChefDispositif || isLogistique;
    const canRequestMaterials = isChefDispositif || isLogistique || isGymnaseChefPoste;
    const canManageVehicles = isChefDispositif || isMoyen;
    const canManageUsers = isChefDispositif;
    const canManageIncidents = isChefDispositif || isMoyen;
    const canManageCenters = isChefDispositif || isLogistique;
    const canCreateCenters = isChefDispositif;
    const canManageResources = isChefDispositif || isLogistique;
    const canManageShifts = isChefDispositif || isLogistique || isGymnaseChefPoste;
    const canConsumeResources = isChefDispositif || isLogistique || isGymnaseChefPoste;
    const canManageOrdresMission = isChefDispositif || isCommune;

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        refreshUser: fetchUser,
        api: axiosInstance,
        // Role checks
        isChefDispositif,
        isLogistique,
        isMoyen,
        isGymnaseChefPoste,
        isGymnaseAdmin,
        isCommune,
        // Permission checks
        canManagePersons,
        canManageLogistics,
        canRequestMaterials,
        canManageVehicles,
        canManageUsers,
        canManageIncidents,
        canManageCenters,
        canCreateCenters,
        canManageResources,
        canManageShifts,
        canConsumeResources,
        canManageOrdresMission
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
